'use client';

import useToggle from '@unifyapps/hooks/useToggle';
import { clsx } from 'clsx';
import type { CSSProperties, PropsWithChildren } from 'react';
import { useCallback, useRef } from 'react';
import useForkRef from '../../../hooks/useForkRef';
import type { TooltipModes, TooltipProps } from '../../Tooltip';
import { Tooltip } from '../../Tooltip';
import Typography from '../Typography';
import type { TypographyProps } from '../types';

export const getTruncateProps = (maxNumberOfLines: number, className?: string) => {
  if (maxNumberOfLines === 1) {
    return { className: clsx('truncate', className) };
  }
  return { maxNumberOfLines, className };
};

// Utility function to determine if content overflows
const isContentTruncated = (element: HTMLElement | null, maxNumberOfLines: number) => {
  if (!element) return false;
  if (maxNumberOfLines === 1) {
    return element.scrollWidth > element.clientWidth;
  }
  return element.scrollHeight > element.clientHeight;
};

export default function TypographyWithTooltip({
  children,
  maxNumberOfLines = 1,
  className,
  tooltipClassName,
  tooltipStyle,
  enterDelay = 0,
  enterNextDelay = 0,
  mode,
  placement = 'top',
  leaveDelay,
  titleNode,
  slotProps,
  ref,
  disablePortal = false,
  ...typographyProps
}: PropsWithChildren<TypographyProps> & {
  enterDelay?: number;
  enterNextDelay?: number;
  leaveDelay?: number;
  tooltipClassName?: string;
  tooltipStyle?: CSSProperties;
  placement?: TooltipProps['placement'];
  titleNode?: React.ReactNode;
  mode?: 'light' | 'dark';
  slotProps?: TooltipProps['slotProps'];
  ref?: React.RefCallback<HTMLElement | null> | null;
  /**
   * If `true`, the content will be rendered inside its parent container
   * instead of being portaled to the `document.body` (default).
   *
   * Useful when you need the element (e.g., Tooltip, Popover, Modal)
   * to stay within a specific container's stacking context.
   */
  disablePortal?: boolean;
}) {
  const typographyRef = useRef<HTMLDivElement>(null);
  const [isTooltipVisible, { on: showTooltip, off: hideTooltip }] = useToggle(false);

  const typographyForkedref = useForkRef(typographyRef, ref);

  const truncateProps = getTruncateProps(maxNumberOfLines, className);

  // Function to determine tooltip visibility
  const updateTooltipVisibility = useCallback(() => {
    const element = typographyRef.current;
    if (element && maxNumberOfLines) {
      if (isContentTruncated(element, maxNumberOfLines)) {
        showTooltip();
      } else {
        hideTooltip();
      }
    }
  }, [maxNumberOfLines, showTooltip, hideTooltip]);

  return (
    <Tooltip
      className={tooltipClassName}
      disablePortal={disablePortal}
      enterDelay={enterDelay}
      enterNextDelay={enterNextDelay}
      leaveDelay={leaveDelay}
      mode={mode as TooltipModes | undefined}
      onClose={hideTooltip}
      onOpen={updateTooltipVisibility}
      open={isTooltipVisible}
      placement={placement}
      slotProps={slotProps}
      style={tooltipStyle}
      title={children}
      titleNode={titleNode}
    >
      <Typography ref={typographyForkedref} {...typographyProps} {...truncateProps}>
        {children}
      </Typography>
    </Tooltip>
  );
}
